import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Scales } from "@phosphor-icons/react";

/**
 * Pick the rate tier that applies to a package weighing `lbs` pounds:
 * the smallest tier >= ceil(lbs); falls back to the heaviest tier.
 */
export function matchTier(rates, lbs) {
  if (!rates?.length || !lbs || lbs <= 0) return null;
  const target = Math.max(1, Math.ceil(lbs));
  const sorted = [...rates].sort((a, b) => a.lbs - b.lbs);
  return sorted.find((t) => t.lbs >= target) || sorted[sorted.length - 1];
}

export function RateTableViewer({ open, onOpenChange, rates, currency, companyName }) {
  const [q, setQ] = useState("");
  const sym = currency === "USD" ? "US$" : "J$";
  const sorted = [...(rates || [])].sort((a, b) => a.lbs - b.lbs);
  const filtered = q.trim() ? sorted.filter((t) => String(t.lbs).startsWith(q.trim())) : sorted;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-[95vw] h-[80vh] flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="rate-table-viewer"
      >
        <div className="px-5 pt-5 pb-4 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-cyan-600 text-white flex items-center justify-center shrink-0">
              <Scales size={14} weight="bold" />
            </div>
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: "Work Sans" }}>
              Shipping rates
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs mt-1.5">
            {companyName ? `${companyName}'s` : "Your courier's"} price for each package weight ({currency || "JMD"}).
          </DialogDescription>
          <div className="relative mt-3">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by weight, e.g. 25"
              className="h-9 pl-9 text-sm"
              inputMode="numeric"
              data-testid="rate-search-input"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-10" data-testid="rate-table-empty">
              No tiers match &quot;{q}&quot;
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-100 dark:border-zinc-800">
                  <th className="text-left py-2">Weight</th>
                  <th className="text-right py-2">Price</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.lbs}
                    className="border-b border-zinc-50 dark:border-zinc-800/50 last:border-0"
                    data-testid={`rate-row-${t.lbs}`}
                  >
                    <td className="py-2 font-medium text-zinc-700 dark:text-zinc-300">
                      {t.lbs} lb{t.lbs === 1 ? "" : "s"}
                    </td>
                    <td className="py-2 text-right font-semibold text-zinc-900 dark:text-zinc-100 font-mono">
                      {sym}
                      {Number(t.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
